import type { GroupStatus, PaymentRun, RunGroup, RunStatus, RunTotals } from '../../lib/paymentRunsApi';
import { formatReportDay, formatReportMoney } from '../accountsPayableReports/format';

/**
 * How a payment run reads on screen and in a bank list. Display only: every figure is the
 * server's, and nothing here adds one currency to another (D49 phase 4).
 */

export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  draft: 'Draft',
  partial: 'Partly recorded',
  completed: 'Recorded',
  cancelled: 'Cancelled',
};

export const RUN_STATUS_TONE: Record<RunStatus, string> = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  partial: 'bg-amber-50 text-amber-800 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  cancelled: 'bg-slate-50 text-slate-500 border-slate-200',
};

export const GROUP_STATUS_LABEL: Record<GroupStatus, string> = {
  pending: 'Not recorded yet',
  recorded: 'Recorded',
  failed: 'Not recorded',
};

/** What a recorded supplier's payment is now: still a payment, or voided since. */
export function paymentLabel(group: Pick<RunGroup, 'status' | 'paymentStatus'>): string {
  if (group.status === 'recorded' && group.paymentStatus === 'void') return 'Voided';
  return GROUP_STATUS_LABEL[group.status];
}

/** "TTD 630.00, USD 70.00": one figure per currency, in the server's order. */
export function moneyByCurrency(totals: Array<Pick<RunTotals, 'currency' | 'amount'>>): string {
  if (!totals.length) return '--';
  return totals.map((t) => formatReportMoney(t.amount, t.currency)).join(', ');
}

/** The paid-to-date and still-to-pay lines of a run's totals, for the header. */
export function remainingByCurrency(totals: RunTotals[]): string {
  const open = totals.filter((t) => t.remaining > 0);
  return open.length ? open.map((t) => formatReportMoney(t.remaining, t.currency)).join(', ') : 'nothing';
}

export const BANK_LIST_HEADERS = [
  'Run',
  'Payment reference',
  'Supplier',
  'Currency',
  'Amount',
  'Payment date',
  'Method',
  'Invoices',
  'Status',
];

/**
 * The bank list: one row per supplier's payment, what to pay and how it will be referenced. It
 * carries no bank details (the business does not keep them here); the reference is what the
 * supplier is told to look for on their statement.
 */
export function bankListRows(run: PaymentRun): Array<Record<string, string>> {
  return run.groups.map((g) => ({
    Run: run.runNumber,
    'Payment reference': g.reference,
    Supplier: g.supplierName,
    Currency: g.currency,
    Amount: g.amount.toFixed(2),
    'Payment date': formatReportDay(run.paymentDate),
    Method: run.method,
    Invoices: g.lines.map((l) => l.invoiceNumber).join('; '),
    Status: paymentLabel(g),
  }));
}

export function bankListFileName(run: PaymentRun): string {
  return `payment-run-${run.runNumber}-bank-list.csv`;
}
